import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppSettings } from '@/app/core/utils/app.settings';
import { HttpService } from '@/app/core/services/http/http.service';
import { ADMIN_SERVICES } from '@/app/core/const/http.const';
import {
  INominacionesByContratoResponse,
  INominacionMercadoSecundarioOptions,
  INominacionMercadoSecundarioPost,
  INominacionMercadoSecundarioPut,
} from '@/app/core/interfaces/nominacion-mercado-secundario.interface';

@Injectable({
  providedIn: 'root',
})
export class NominacionMercadoSecundarioService {
  constructor(
    private http: HttpService,
    private httpClient: HttpClient,
    private appSetting: AppSettings
  ) {}

  private get baseUrl(): string {
    return `${this.appSetting.adminURL}/${ADMIN_SERVICES.NOMINATION_MARKET_CONTRACTS}`;
  }

  /** GET /options?contratoMercadoSecundarioUuid=:uuid&puntoSalidaUuid=:uuid&demandaUuid=:uuid */
  async getOptions(
    contratoMercadoSecundarioUuid: string,
    puntoSalidaUuid?: string | null,
    demandaUuid?: string | null
  ): Promise<INominacionMercadoSecundarioOptions> {
    const params = new URLSearchParams();
    params.set('contratoMercadoSecundarioUuid', contratoMercadoSecundarioUuid);
    if (puntoSalidaUuid) {
      params.set('puntoSalidaUuid', puntoSalidaUuid);
    }
    if (demandaUuid) {
      params.set('demandaUuid', demandaUuid);
    }
    const url = `${this.baseUrl}/options?${params.toString()}`;
    try {
      return (await this.http.getService(
        url
      )) as INominacionMercadoSecundarioOptions;
    } catch {
      return {};
    }
  }

  /** GET /by-contrato/:contratoUuid?fecha=:yyyy-MM-dd */
  async getNominacionesByContrato(
    contratoUuid: string,
    fecha?: string | null
  ): Promise<INominacionesByContratoResponse> {
    const params = fecha ? `?fecha=${encodeURIComponent(fecha)}` : '';
    const url = `${this.baseUrl}/by-contrato/${contratoUuid}${params}`;
    try {
      const response = await firstValueFrom(
        this.httpClient.get<INominacionesByContratoResponse>(url)
      );
      return response;
    } catch {
      return {
        infoContrato: {
          uuid: contratoUuid,
          noContrato: '',
          fechaInicio: '',
          fechaFin: '',
          cantidadConUnidad: '',
        },
        nominaciones: [],
      };
    }
  }

  /** POST / */
  async createNominacion(
    data: INominacionMercadoSecundarioPost
  ): Promise<{ res: unknown; status: true } | { err: unknown; status: false }> {
    try {
      const res = await this.http.postService(this.baseUrl, data);
      return { res, status: true };
    } catch (err) {
      return { err, status: false };
    }
  }

  /** PUT /:uuid */
  async updateNominacion(
    uuid: string,
    data: INominacionMercadoSecundarioPut
  ): Promise<{ res: unknown; status: true } | { err: unknown; status: false }> {
    try {
      const res = await this.http.putService(`${this.baseUrl}/${uuid}`, data);
      return { res, status: true };
    } catch (err) {
      return { err, status: false };
    }
  }

  /** DELETE /:uuid */
  async deleteNominacion(
    uuid: string
  ): Promise<true | { err: unknown; status: false }> {
    try {
      await this.http.deleteService(`${this.baseUrl}/${uuid}`);
      return true;
    } catch (err) {
      return { err, status: false };
    }
  }
}
